import CookieConsent from '@components/CookieConsent/CookieConsent';
import DraftModeBanner from '@components/DraftMode/Banner';
import GoogleTagManager from '@components/GoogleTagManager/GoogleTagManager';
import { CurrencyProvider } from '@contexts/CurrencyContext';
import { WithAuthSync } from '@lib/api/auth/WithAuthSync';
import { showCookieConsent } from '@lib/cookie';
import { FlagsProvider } from '@lib/flags/amplitude/client';
import { isServerSideRendered } from '@lib/ssr';
import getFontVariables from '@lib/util/getFontVariables';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import React from 'react';
import 'ui-components/src/styles/tailwind.css';
import { DEFAULT_LOCALE } from 'utils';
import { getLocalizedDefaultSeo } from '../../next-seo.config';
// custom coveo atomic styling overrides
import { WithAnalytics } from '@components/Analytics/WithAnalytics';
import { SWRProvider } from '@components/SWRProvider/SWRProvider';
import { PagesRouterProvider } from '@contexts/Router/PagesRouterProvider';
import { ServerTimeProvider } from '@contexts/ServerTimeContext';
import { AtomicSearchProvider } from '@contexts/coveo/AtomicSearchContext';
import { AuthSessionProvider } from '@lib/api/auth/AuthSessionProvider';
import Head from 'next/head';
import { useRouter } from 'next/router';
import '../styles/coveoCustomCss.css';

/**
 * We don't want to print accessibility errors in the console for production.
 */
const AXE_DEBOUNCE_TIME = 1000;

if (
  process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' &&
  !(typeof window !== 'undefined' && window.Cypress) &&
  !isServerSideRendered()
) {
  import('react-dom').then((ReactDOM) => {
    import('@axe-core/react').then((axe) => {
      axe.default(React, ReactDOM, AXE_DEBOUNCE_TIME, {});
    });
  });
}

function App({ Component, pageProps }: AppProps) {
  const fontVariables = getFontVariables();
  /**
   * If a session is in Draft Mode, we want to know about it here so we can:
   * - Show a preview banner globally
   * - Disable the OneTrust Cookie Consent banner
   */
  const isDraftMode = pageProps.draft ?? false;
  const router = useRouter();
  const locale = router.query.locale as string;

  return (
    <AuthSessionProvider session={pageProps.session} refetchOnWindowFocus>
      <WithAuthSync />
      <SWRProvider>
        <FlagsProvider serverSideFlags={pageProps.flagVariants}>
          <PagesRouterProvider>
            <CurrencyProvider>
              <WithAnalytics />
              <ServerTimeProvider>
                <AtomicSearchProvider>
                  <DefaultSeo
                    {...getLocalizedDefaultSeo(locale || DEFAULT_LOCALE)}
                  />
                  <Head>
                    {/* This can be moved out of the head once next 14.1 is updated to, and CookieConsent switches back to Script with beforeInteractive strategy */}
                    {showCookieConsent() && !isDraftMode && (
                      <CookieConsent
                        dataDomain={process.env.NEXT_PUBLIC_ONETRUST_ID}
                      />
                    )}
                  </Head>
                  <GoogleTagManager
                    containerId={process.env.NEXT_PUBLIC_GTM_ID}
                  />
                  <div
                    id="page-root"
                    className={`${fontVariables} antialiased`}
                  >
                    {isDraftMode && <DraftModeBanner />}
                    <Component {...pageProps} />
                  </div>
                </AtomicSearchProvider>
              </ServerTimeProvider>
            </CurrencyProvider>
          </PagesRouterProvider>
        </FlagsProvider>
      </SWRProvider>
    </AuthSessionProvider>
  );
}

export default App;
